import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ModelViewerUrbaseeService } from '../services/model-viewer-urbasee.service';
import { ModelViewerArtefactoService } from '../../model-viewer-artefacto/model-viewer-artefacto.service';
import { Project, TextureCategory } from '../../openapi';
import { tap } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Element } from '../entities/element.entity';
import { ElementVariant } from '../entities/element-variant.entity';
import { ProjectMetadata } from '../entities/project-metadata.entity';
import { Keyword } from '../entities/keyword.entity';

/**
 * Composant pour afficher un model viewer urbasee
 *
 * Se charge de s'identifier et de récupérer le fichier du model pour l'afficher
 * dans un model viewer artefacto (ce dernier utilise model-viewer de google)
 */

@Component({
  selector: 'app-model-viewer-urbasee',
  templateUrl: './model-viewer-urbasee.component.html',
  styleUrl: './model-viewer-urbasee.component.scss',
})
export class ModelViewerUrbaseeComponent implements OnInit, OnChanges {
  @Input('project-id') projectId?: number;
  @Input('token') token?: string;
  @Input('application-type-code') applicationTypeCode?: string;

  @Input('width') public width: string = '100%';
  @Input('height') public height: string = '50rem';

  @Input('main-element') public mainElement?: Element;
  @Input('main-element-variant') public mainElementVariant?: ElementVariant;

  @Input('force-ar') public forceAr: boolean = false;

  @Input('accessory-element') public accessoryElement?: Element;
  @Input('accessory-element-variant')
  public accessoryElementVariant?: ElementVariant;

  @Output('elements-loaded') elementsLoaded = new EventEmitter<Element[]>();
  @Output('project-loaded') projectLoaded = new EventEmitter<Project>();
  @Output('textures-categories-loaded') texturesCategoriesLoaded =
    new EventEmitter<TextureCategory[]>();
  @Output('keywords-loaded') keywordsLoaded = new EventEmitter<Keyword[]>();

  @Output('model-viewer-ready') modelViewerReady = new EventEmitter<any>();

  @Output('can-activate-ar') canActivateAr = new EventEmitter<boolean>();

  public modelFileUrl?: string | Blob | null;

  public enabled = false;

  public elements: Element[] = [];

  public project?: Project;
  public textureCategories?: TextureCategory[];
  public keywords?: Keyword[];

  constructor(
    private service: ModelViewerUrbaseeService,
    private modelViewerArtefactoService: ModelViewerArtefactoService,
    private httpClient: HttpClient
  ) {}
  ngOnInit(): void {
    if (!this.projectId) {
      throw new Error(`[Model-Viewer-Urbasee] project-id missing`);
    }
    if (!this.applicationTypeCode && !this.token) {
      throw new Error(
        `[Model-Viewer-Urbasee] application-type-code and token are missing`
      );
    }

    this.service
      .fetchJwt(this.token, this.applicationTypeCode)
      .subscribe(() => {
        this.service.fetchProject(this.projectId!).subscribe((project) => {
          console.log(`[Model-Viewer-Urbasee] Project loaded`, project);
          this.project = project;
          this.projectLoaded.emit(this.project);
          // Get texture categories
          this.service
            .fetchTextures(project.applicationId!)
            .subscribe((textureCategories) => {
              this.textureCategories = textureCategories;
              this.texturesCategoriesLoaded.emit(textureCategories);
              this.populateElements(textureCategories);
            });
          // Get model file blob (login, fetch project, download)
          this.service.fetchModel3dBlob(this.projectId!).subscribe((data) => {
            this.modelFileUrl = URL.createObjectURL(data);
            this.enabled = true;
          });
          // Get keywords
          this.service
            .fetchKeywords(project.applicationId!)
            .subscribe((keywords) => {
              this.keywords = keywords;
              this.keywordsLoaded.emit(keywords);
            });
        });
      });
  }

  populateElements(textureCategories: TextureCategory[]) {
    // Parse metadata of Project Urbasee
    const configurationObject: any = JSON.parse(this.project?.metadata || '{}');
    const configuration: ProjectMetadata = <ProjectMetadata>configurationObject;
    this.elements = <Element[]>configuration.elements;
    console.log('[Model-Viewer-Urbasee] Configuration loaded:', configuration);
    if (this.elements.length === 0) {
      console.warn('[Model-Viewer-Urbasee] No elements found in configuration');
    }
  }

  // Apply changes when attributes are updated
  ngOnChanges(changes: SimpleChanges): void {
    // Update structure color
    if (
      changes['mainElementVariant'] &&
      !changes['mainElementVariant'].firstChange
    ) {
      if (!this.mainElement) {
        console.warn(
          '[Model-Viewer-Urbasee] mainElementVariant set but no mainElement defined. skip.'
        );
        return;
      }
      this._applyVariantOnElement(
        this.mainElement,
        changes['mainElementVariant'].currentValue
      );
    }

    // Disable previous accessoryElement
    if (
      changes['accessoryElement'] &&
      changes['accessoryElement'].previousValue &&
      !changes['accessoryElement'].firstChange
    ) {
      this.enableElement(changes['accessoryElement'].previousValue, false);
    }

    // Enable currentaccessoryElement
    if (
      changes['accessoryElement'] &&
      changes['accessoryElement'].currentValue &&
      !changes['accessoryElement'].firstChange
    ) {
      this.enableElement(changes['accessoryElement'].currentValue, true);
    }

    // Update accessoryName color (skip if no accessory defined)
    if (
      changes['accessoryElementVariant'] &&
      !changes['accessoryElementVariant'].firstChange
    ) {
      if (!this.accessoryElement) {
        console.warn(
          '[Model-Viewer-Urbasee] accessoryElementVariant set but no accessoryElement defined. skip.'
        );
        return;
      }
      this._applyVariantOnElement(
        this.accessoryElement,
        changes['accessoryElementVariant'].currentValue
      );
    }
  }

  /**
   * Model 3D is fully loaded on model-viewer-artefacto
   */
  modelLoaded(e: any) {
    console.log('[Model-Viewer-Urbasee] Model loaded');

    // Disable all textures in elements with boolean disableOnLoad
    this.elements?.forEach((element) => {
      if (element.disableOnLoad) {
        console.log(
          `[Model-Viewer-Urbasee] Disable all materialNames in element ${element.name} and in variants`
        );
        // Disable all materialNames of element
        this._enableElementNames(element.materialNames, false);
        // Disable all materialNames of variants
        element.variants?.forEach((variant) => {
          variant.textures?.forEach((texture) => {
            if (element.disableOnLoad) {
              this._enableElementNames(texture.materialNames, false);
            }
          });
        });
      }
    });

    // Apply default colors if not hidden on boot
    this.elements?.forEach((element) => {
      if (!element.disableOnLoad) {
        console.log(
          `[Model-Viewer-Urbasee] Apply default variant on element ${element.name} not hidden`
        );
        const defaultVariant = element.variants?.find(
          (variant) => variant.applyOnLoad
        );
        if (defaultVariant) {
          this._applyVariantOnElement(element, defaultVariant);
        }
      }
    });

    this.elementsLoaded.emit(this.elements);
    this.modelViewerReady.emit(e);
  }

  /**
   * Enable or disable an element by hide/show texture
   */
  enableElement(element: Element, enable: boolean) {
    console.log(
      `[Model-Viewer-Urbasee] Enable element ${element.name} to:`,
      !!enable
    );
    // Enable materialNames of element
    this._enableElementNames(element.materialNames, enable);

    // Apply variant on materialNames of element
    element.variants?.forEach((variant) => {
      variant.textures?.forEach((texture) => {
        this._enableElementNames(texture.materialNames, enable);
      });
    });

    const defaultElementVariant = element.variants?.find(
      (variant) => variant.applyOnLoad
    );
    if (enable && defaultElementVariant) {
      this._applyVariantOnElement(element, defaultElementVariant);
    }
  }

  /************************************************************************ */

  /**
   * Enable or disable an element by hide/show texture
   * @param accessoryReference
   * @param enable
   */
  private _enableElementNames(elementNames: string[], enable: boolean) {
    // Disable all accesories defined by TextureCategories names
    elementNames?.forEach((elementName) => {
      // Enable the accessory wanted
      this.modelViewerArtefactoService.enableTexture(elementName, enable);
    });
  }

  /**
   * Apply an ElementVariant on an Element
   * @param element
   * @param variant
   */
  _applyVariantOnElement(element: Element, variant: ElementVariant) {
    // Enable materialNames of element
    this._enableElementNames(element.materialNames, true);

    // Apply variant of model3d
    this.modelViewerArtefactoService.applyVariant(variant.variantName);

    // Apply variant on materialNames of element
    variant.textures?.forEach((texture) => {
      this._applyTextureColorOnMaterials(
        texture.materialNames,
        texture.textureColorId,
        texture.forceCleanTexture
      );
    });
  }

  private _applyTextureColorOnMaterials(
    materialNames: string[],
    textureColorId: number,
    forceCleanTexture = false
  ) {
    const textureColor = <any>this._getTextureColorById(textureColorId);
    if (textureColor.hasMotifTexture) {
      // Apply a motif
      this._applyTextureColorMotifOnMaterial(
        materialNames,
        textureColor.id,
        forceCleanTexture
      );
    } else {
      // Apply color
      this._applyTextureColorRgbOnMaterial(
        materialNames,
        textureColor.rgbs[0].red,
        textureColor.rgbs[0].green,
        textureColor.rgbs[0].blue,
        forceCleanTexture
      );
    }
  }

  private _getTextureColorById(textureColorId: number) {
    let found = undefined;
    this.textureCategories?.forEach((textureCategory) => {
      textureCategory.textureColors?.forEach((textureColor) => {
        if (textureColor.id === textureColorId) {
          found = textureColor;
        }
      });
    });
    if (!found) {
      console.error(
        `[Model-Viewer-Urbasee] Texture category ${textureColorId} not found`
      );
    }

    return found;
  }

  private _applyTextureColorRgbOnMaterial(
    materialNames: string[],
    r: number,
    g: number,
    b: number,
    forceCleanTexture = false
  ) {
    //WORKAROUND: decrease lighting
    const fixLevel = -1;
    const actualColorLevel = (255 - (r + g + b) / 3) / 255;
    const rFixed = r + r * fixLevel * actualColorLevel;
    const gFixed = g + g * fixLevel * actualColorLevel;
    const bFixed = b + b * fixLevel * actualColorLevel;

    materialNames.forEach((materialName) => {
      // Pre reset material from original
      this.modelViewerArtefactoService.resetMaterial(
        materialName,
        forceCleanTexture
      );
      this.modelViewerArtefactoService.applyColorOnMaterial(
        materialName,
        rFixed,
        gFixed,
        bFixed
      );
    });
  }

  private _applyTextureColorMotifOnMaterial(
    materialNames: string[],
    colorId: number,
    forceCleanTexture = false
  ) {
    materialNames.forEach((materialName) => {
      // Pre reset material from original
      this.modelViewerArtefactoService.resetMaterial(
        materialName,
        forceCleanTexture
      );
      this.modelViewerArtefactoService.applyColorOnMaterial(
        materialName,
        255,
        255,
        255
      );
      this.httpClient
        .get(
          `${environment.apiUrl}/services/v4.0/texture-color/${colorId}/thumb`,
          { responseType: 'blob' }
        )
        .pipe(
          tap((blob) => {
            this.modelViewerArtefactoService.applyTextureOnMaterial(
              materialName,
              blob
            );
          })
        )
        .subscribe();
    });
  }

  onCanActivateAr(e: any) {
    console.log('[Model-Viewer-Urbasee] Can activate AR', e);
    this.canActivateAr.emit(e);
  }
}
